/*
 * withRouter — local replacement for the HOC react-router removed in v6.
 *
 * react-router v7 is hooks-only, but several containers here are class
 * components that need router access. Rather than rewrite them as function
 * components (a much larger, riskier diff on top of an already two-major
 * router upgrade), this HOC injects the same three things the class components
 * actually used, via hooks.
 *
 * Injected props:
 *   location  — same shape as v5 (pathname, search, hash)
 *   params    — v5's `match.params`
 *   navigate  — v7's navigate function; replaces v5's injected `history`
 *
 * Note the deliberate difference from v5: there is no `history` prop and no
 * `match` object. Call sites use `props.navigate('/x')` instead of
 * `props.history.push('/x')`, so nothing silently depends on a v5-shaped
 * object that no longer exists.
 */

import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const withRouter = (Component) => {
  const Wrapped = (props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    return (
      <Component
        {...props}
        location={location}
        navigate={navigate}
        params={params}
      />
    );
  };

  Wrapped.displayName = `withRouter(${
    Component.displayName || Component.name || "Component"
  })`;

  return Wrapped;
};

export default withRouter;
